import { Link } from "react-router-dom";
import { Package, UserPlus, SlidersHorizontal, RefreshCw, Zap, Lock, Check } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";

const STEPS = [
  {
    icon: Package,
    title: "Order your NFC products",
    body: "Give us the review link you want customers to open at checkout.",
  },
  {
    icon: UserPlus,
    title: "Create your free portal account",
    body: "Use the same email you ordered with, and your products appear automatically.",
  },
  {
    icon: SlidersHorizontal,
    title: "Choose your plan",
    body: "Stay on Starter for free, or upgrade anytime for editing and analytics.",
  },
];

const PLANS = [
  {
    key: "starter",
    name: "Starter",
    price: "$0",
    features: [
      "Your products send customers to the review link you provided",
      "Fixed destination (can't be changed from the portal)",
      "No analytics dashboard",
      "Add-on slots not available",
    ],
    cta: "Create free account",
    highlight: false,
  },
  {
    key: "essential",
    name: "Essential",
    price: "$9.99",
    features: [
      "3 registered products included",
      "Change where your products send customers anytime, without replacing them",
      "Tap analytics + monthly email report",
      "Add-on slots available ($1 per extra product per month)",
    ],
    cta: "Choose Essential",
    highlight: false,
  },
  {
    key: "growth",
    name: "Growth",
    price: "$19.99",
    features: [
      "10 registered products included",
      "Everything in Essential, plus:",
      "Staff attribution — see which team member's product gets tapped",
      "Location labels and full tap history",
    ],
    cta: "Choose Growth",
    highlight: true,
  },
];

const GOOD_TO_KNOW = [
  {
    icon: RefreshCw,
    text: "Your NFC products keep working on any plan, even if you cancel. Paid features continue through your billing period, then your account moves to Starter.",
  },
  {
    icon: Zap,
    text: "Upgrades take effect right away; downgrades take effect at your next renewal.",
  },
  {
    icon: Lock,
    text: "Revora reports taps and activity on your products — not confirmed Google reviews.",
  },
];

const INCLUDED = ["NFC chip", "Destination setup", "Shipping"];

export const PortalPlans = () => (
  <section id="plans" className="relative bg-ink overflow-hidden" data-testid="portal-plans-section">
    <div className="h-[3px] bg-volt" />
    {/* subtle NFC signal-ring motif */}
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "repeating-radial-gradient(circle at 88% 12%, rgba(0,176,224,0.09) 0 2px, transparent 2px 90px)",
      }}
    />
    <div className="relative max-w-7xl mx-auto px-6 py-28 sm:py-36">
      <Chapter number="06" label="The Portal" dark />
      <Reveal>
        <h2 className="rvr-title font-display text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.05] max-w-3xl">
          Your products work with the Revora business portal.
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="text-white/55 text-lg mt-4 max-w-2xl">
          Every Revora NFC product works on its own. A plan adds control and insights.
        </p>
      </Reveal>

      {/* 3-step strip */}
      <div className="grid sm:grid-cols-3 gap-6 mt-14" data-testid="portal-steps">
        {STEPS.map((s, i) => (
          <Reveal key={s.title} delay={0.08 * i}>
            <div className="flex sm:flex-col gap-4 items-start">
              <span className="shrink-0 w-11 h-11 rounded-full border border-volt/60 text-volt font-display font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <div>
                <p className="font-display text-lg font-bold text-white flex items-center gap-2">
                  <s.icon className="w-5 h-5 text-volt" /> {s.title}
                </p>
                <p className="text-sm text-white/55 mt-1.5 leading-relaxed">{s.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* plan cards */}
      <div className="grid md:grid-cols-3 gap-5 mt-16" data-testid="portal-plan-cards">
        {PLANS.map((p, i) => (
          <Reveal key={p.key} delay={0.08 * i} className="h-full">
            <div
              data-testid={`plan-card-${p.key}`}
              className={`h-full flex flex-col rounded-md p-7 bg-white/[0.04] border transition-colors duration-300 ${
                p.highlight ? "border-volt shadow-[0_0_40px_rgba(0,176,224,0.15)]" : "border-white/10 hover:border-white/25"
              }`}
            >
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-volt">{p.name}</p>
              <p className="mt-3">
                <span className="font-display text-4xl font-black text-white">{p.price}</span>
                <span className="text-white/45 text-sm">/month</span>
              </p>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-white/70 leading-relaxed">
                    <Check className="w-4 h-4 text-volt shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/portal"
                data-testid={`plan-cta-${p.key}`}
                className={`mt-8 inline-flex items-center justify-center min-h-[44px] px-6 rounded-full text-sm font-bold transition-colors duration-200 ${
                  p.highlight
                    ? "bg-volt text-ink hover:bg-white"
                    : "border border-white/25 text-white hover:bg-white hover:text-ink"
                }`}
              >
                {p.cta}
              </Link>
            </div>
          </Reveal>
        ))}
      </div>

      {/* add-on callout */}
      <Reveal delay={0.1}>
        <div className="mt-8 border border-volt/40 bg-volt/10 rounded-md p-6" data-testid="addon-callout">
          <p className="text-sm text-white/85 leading-relaxed">
            <strong className="text-white">Need more than your plan includes?</strong> Essential and Growth subscribers can add
            extra registered product slots for <strong className="text-volt">$1 per product per month</strong> (1–50 extra slots).
          </p>
          <p className="text-xs text-white/50 mt-2">Each NFC product you manage in the portal uses one slot.</p>
        </div>
      </Reveal>

      {/* good to know */}
      <div className="grid sm:grid-cols-3 gap-6 mt-12" data-testid="good-to-know">
        {GOOD_TO_KNOW.map((g) => (
          <div key={g.text.slice(0, 20)} className="flex items-start gap-3">
            <g.icon className="w-5 h-5 text-volt shrink-0 mt-0.5" />
            <p className="text-xs text-white/55 leading-relaxed">{g.text}</p>
          </div>
        ))}
      </div>

      {/* included with every product */}
      <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="included-row">
        <span className="text-xs font-bold tracking-[0.15em] uppercase text-white/40">Included with every product</span>
        {INCLUDED.map((f) => (
          <span key={f} className="inline-flex items-center gap-1.5 text-xs text-white/65">
            <Check className="w-3.5 h-3.5 text-volt" /> {f}
          </span>
        ))}
      </div>
    </div>
  </section>
);
